import { getFallbackMenuResponse } from '@/api/fallbackResponse';
import { useI18n } from '@/hooks/useI18n';
import { useLanguageSwitch } from '@/hooks/useLanguageSwitch';
import { SCOPE_CLASS, usePortalScope } from '@/hooks/usePortalScope';
import { User } from '@vaisala/rockhopper-icons/icons/User';
import { Menu } from '@vaisala/rockhopper-vaisala-com-components/Menu/index';
import React, { useCallback, useRef } from 'react';
import styles from './HankoMenu.module.scss';
import { MenuItem } from './MenuItem';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Fallback menu for ErrorBoundary. Needs its own usePortalScope call since
 * HankoMenuContent's instance (and its scoping) unmounts with the crash.
 */
function ErrorFallbackMenu() {
  const rootRef = useRef<HTMLDivElement>(null);
  const getRoot = useCallback(() => rootRef.current, []);
  usePortalScope(true, getRoot);

  // Even in the crash fallback, resolve the selected locale so the menu (and
  // its trigger label) match the user's language. Unsupported locales resolve
  // to English.
  const { locale } = useLanguageSwitch();
  const { t } = useI18n(locale);

  const menuItems = getFallbackMenuResponse(t).menuItems;

  return (
    <div ref={rootRef} className={SCOPE_CLASS}>
      <Menu
        _rootProps={{ modal: false }}
        renderTrigger={(props) => (
          <button
            {...props}
            aria-label={t('menu.ariaUserMenu')}
            type="button"
            className={styles.trigger}
          >
            <User width={24} height={24} />
          </button>
        )}
      >
        <Menu.Content className={styles.content}>
          {menuItems.map((item, index) => (
            <MenuItem key={`${item.type}-${index}`} item={item} locale={locale} />
          ))}
        </Menu.Content>
      </Menu>
    </div>
  );
}

/**
 * React error boundary wrapping the HankoMenu component tree.
 *
 * Catches rendering errors and renders fallback menu items using the same
 * MenuItem component and Menu layout, so the user can still log in.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <ErrorFallbackMenu />;
    }
    return this.props.children;
  }
}
